import { useTheme } from '@core/hooks/useTheme';
import { logger } from '@core/logging';
import { generateCorrelationId } from '@core/utils/correlationUtils';
import { StyleProvider as VanillaExtractStyleProvider, useStyleProvider as useVanillaStyleProvider } from '@soteria/design-system-ve';
import type { Theme } from '@soteria/design-system-ve';
import { LogCategory } from '@soteria/logger/browser';
import { type ReactNode, useEffect } from 'react';

import '@soteria/design-system-ve/style.css';

/**
 * Custom hook that combines the Redux state with the StyleProvider context
 */
export function useStyleProvider() {
  const { theme, setCompleteTheme } = useTheme();
  const vanillaContext = useVanillaStyleProvider();

  // Sync the vanilla-extract theme with Redux when it changes from vanilla-extract
  useEffect(() => {
    if (vanillaContext.theme !== theme) {
      const correlationId = generateCorrelationId();

      logger.debug('Syncing theme from vanilla-extract to Redux', {
        category: LogCategory.SYSTEM,
        operation: 'syncTheme',
        correlationId,
        previousTheme: theme,
        newTheme: vanillaContext.theme,
        source: 'vanilla-extract',
      });

      setCompleteTheme(vanillaContext.theme);
    }
  }, [vanillaContext.theme, theme, setCompleteTheme]);

  // Return a combined context that uses Redux for state but delegates to vanilla-extract for setTheme
  return {
    theme,
    setTheme: (newTheme: Theme) => {
      setCompleteTheme(newTheme);
      vanillaContext.setTheme(newTheme);
    },
  };
}

export function StyleProvider({ children }: { children: ReactNode }) {
  // Get the theme from Redux and hooks to update it
  const { setTheme, setCompleteTheme, setHighContrast } = useTheme();

  // Initialize theme from localStorage on component mount
  useEffect(() => {
    const correlationId = generateCorrelationId();

    // Get theme from localStorage or use default
    const savedTheme = typeof localStorage !== 'undefined' ? (localStorage.getItem('theme') as Theme) || 'dark' : 'dark';

    logger.debug('Initializing theme from localStorage', {
      category: LogCategory.SYSTEM,
      operation: 'initializeTheme',
      correlationId,
      savedTheme,
      hasLocalStorage: typeof localStorage !== 'undefined',
      source: 'localStorage',
    });

    // Set the complete theme in Redux
    setCompleteTheme(savedTheme);

    // Set the theme name in Redux (light, dark, or system)
    const themeName = savedTheme.includes('hc-') ? (savedTheme.replace('hc-', '') as 'light' | 'dark') : (savedTheme as 'light' | 'dark');

    setTheme(themeName);

    // Set high contrast state in Redux
    const isHighContrast = savedTheme.toString().includes('hc-');

    setHighContrast(isHighContrast);

    logger.debug('Theme initialization completed', {
      category: LogCategory.SYSTEM,
      operation: 'initializeTheme',
      correlationId,
      themeName,
      isHighContrast,
      finalTheme: savedTheme,
    });
  }, [setTheme, setCompleteTheme, setHighContrast]);

  // Get the saved theme from localStorage or use a default
  const savedTheme = typeof localStorage !== 'undefined' ? (localStorage.getItem('theme') as Theme) || 'dark' : 'dark';

  // Use the saved theme directly for the vanilla-extract StyleProvider
  const themeToUse = savedTheme;

  return <VanillaExtractStyleProvider defaultTheme={themeToUse}>{children}</VanillaExtractStyleProvider>;
}
